import type AssistantViewController from '../AssistantView/AssistantViewController';
import { type AttachmentStatus } from '../AssistantView/AssistantViewController';
import Attachment from '../../../framework/models/Attachment';
import { randomId } from '@aha-app/mvc';
import { showWarningMessage } from 'javascripts/messages';

const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'text/*',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/excel',
];

// NOTE: File type validation uses MIME type matching with support for wildcard suffix (e.g. text/*)
function isAcceptedFileType(file: File) {
  // Some browsers may provide an empty string for unknown types. Treat as invalid.
  if (!file.type) return false;
  return ACCEPTED_FILE_TYPES.some(accepted => {
    if (accepted.endsWith('/*')) {
      return file.type.startsWith(accepted.slice(0, -1));
    }
    return file.type === accepted;
  });
}

// Open a modal to choose files to upload.
function chooseFiles(controller: AssistantViewController) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPTED_FILE_TYPES.join(',');
  input.multiple = true;

  input.onchange = event => {
    const files = (event.target as HTMLInputElement).files;
    if (files) {
      uploadFiles(controller, files);
    }
  };

  input.click();
}

// Upload an array of files to the server based on choosing files in a modal or dropping.
function uploadFiles(controller: AssistantViewController, files: FileList) {
  for (const file of files) {
    if (!isAcceptedFileType(file)) {
      showWarningMessage(
        `Unsupported file ${file.name}. Only images, txt, markdown, csv, pdf, word, and excel files are allowed`
      );
      continue; // Skip this file
    }
    const attachmentStatus: AttachmentStatus = {
      id: randomId(),
      loading: true,
      detectedContentType: file.type,
    };
    controller.actionAddAttachmentStatus(attachmentStatus);
    void uploadAttachmentFile(controller, attachmentStatus, file);
  }
}

async function uploadAttachmentFile(
  controller: AssistantViewController,
  attachmentStatus: AttachmentStatus,
  file: File
) {
  const response = await fetch(
    `/attachments?qqfile=${encodeURIComponent(file.name)}`,
    {
      method: 'POST',
      headers: {
        'X-CSRF-Token': window.csrfToken(),
        'Content-Type': 'application/octet-stream',
      },
      body: file,
    }
  );
  if (!response.ok) {
    console.warn('Failed to upload attachment');
    return null;
  }

  const result = (await response.json()) as {
    attachmentId: string;
    attachmentUrl: string;
  };
  controller.actionSetAttachmentStatus(
    attachmentStatus,
    new Attachment({
      id: result.attachmentId,
      fileName: file.name,
      downloadUrl: result.attachmentUrl,
      contentType: file.type,
      fileSize: file.size,
    })
  );
}

export { chooseFiles, uploadFiles };
